(function () {
  'use strict';

  var steps = [160, 240, 360, 480, 600, 800, 1000, 1200, 1600, 2000, 2400, 3200, 3840, 4800, 5760];
  var states = new WeakMap();
  var images = new Set();
  var queue = [];
  var active = 0;
  var scheduled = false;

  function urlFor(value) {
    try {
      var url = new URL(value, document.baseURI);
      var shopify = url.hostname === 'cdn.shopify.com' ||
        ((url.origin === window.location.origin || url.hostname === 'montrose-cosmetics.com') && /^\/cdn\/shop\//.test(url.pathname));
      if (!shopify || !/\.(png|jpe?g|webp|avif)(?:$|_)/i.test(url.pathname)) return null;
      return url;
    } catch (error) { return null; }
  }

  function widthOf(url, image) {
    var width = Number(url.searchParams.get('width'));
    if (width > 0) return width;
    var legacy = url.pathname.match(/_(\d+)x(?:\d*)\.(?:png|jpe?g|webp|avif)$/i);
    if (legacy) return Number(legacy[1]);
    var candidates = (image.getAttribute('srcset') || '').split(',');
    for (var i = 0; i < candidates.length; i++) {
      var entry = candidates[i].trim().match(/^(\S+)\s+(\d+)w$/);
      if (entry && new URL(entry[1], document.baseURI).href === url.href) return Number(entry[2]);
    }
    return image.naturalWidth;
  }

  function requiredWidth(image) {
    if (image.checkVisibility && !image.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })) return 0;
    var rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height || rect.bottom < 0 || rect.top > window.innerHeight ||
        rect.right < 0 || rect.left > window.innerWidth) return 0;
    var ratio = image.naturalWidth / image.naturalHeight;
    var style = window.getComputedStyle(image);
    if (style.visibility === 'hidden' || style.display === 'none' || style.opacity === '0') return 0;
    var cssWidth = rect.width;
    if (style.objectFit === 'cover' || style.objectFit === 'fill') cssWidth = Math.max(cssWidth, rect.height * ratio);
    else if (style.objectFit === 'contain') cssWidth = Math.min(cssWidth, rect.height * ratio);
    var requested = Math.ceil(cssWidth * (window.devicePixelRatio || 1));
    return { css: Math.ceil(cssWidth), pixels: requested };
  }

  function sourceFor(image, current) {
    var picture = image.closest('picture');
    if (!picture) return image;
    var sources = picture.querySelectorAll('source');
    for (var i = 0; i < sources.length; i++) {
      var entries = (sources[i].getAttribute('srcset') || '').split(',');
      for (var j = 0; j < entries.length; j++) {
        var value = entries[j].trim().split(/\s+/)[0];
        if (value && new URL(value, document.baseURI).href === current) return sources[i];
      }
    }
    return image;
  }

  function inspect(image) {
    if (!image.isConnected || !image.complete || !image.naturalWidth || !image.naturalHeight) return;
    var needed = requiredWidth(image);
    if (!needed) return;
    var current = image.currentSrc || image.src;
    var url = urlFor(current);
    if (!url) return;
    var key = url.origin + url.pathname + '|' + (url.searchParams.get('v') || '') + '|' + (url.searchParams.get('crop') || '');
    var state = states.get(image);
    if (!state || state.key !== key) { state = { key: key, limit: Infinity, pending: false, attempted: 0 }; states.set(image, state); }
    if (state.pending) return;
    var delivered = widthOf(url, image);
    if (needed.pixels <= delivered || delivered >= state.limit) return;
    var target = steps.find(function (width) { return width >= needed.pixels; }) || 5760;
    target = Math.min(target, state.limit);
    if (target <= delivered || state.attempted === target) return;
    var oldHeight = Number(url.searchParams.get('height'));
    if (oldHeight > 0 && delivered > 0) {
      target = Math.min(target, Math.floor(5760 * delivered / oldHeight));
      url.searchParams.set('height', String(Math.ceil(oldHeight * target / delivered)));
    }
    url.pathname = url.pathname.replace(/_\d+x\d*(?=\.(?:png|jpe?g|webp|avif)$)/i, '');
    url.searchParams.set('width', String(target));
    state.pending = true;
    state.attempted = target;
    queue.push({ image: image, source: sourceFor(image, current), current: current, url: url.href, target: target, delivered: delivered, css: needed.css, state: state });
    drain();
  }

  function drain() {
    while (active < 2 && queue.length) {
      var job = queue.shift();
      if (!job.image.isConnected || (job.image.currentSrc || job.image.src) !== job.current) { job.state.pending = false; continue; }
      active++;
      prepare(job);
    }
  }

  function prepare(job) {
    var probe = new Image();
    probe.decoding = 'async';
    probe.fetchPriority = 'low';
    function finish() { job.state.pending = false; active--; drain(); }
    probe.onerror = finish;
    probe.onload = function () {
      var actual = probe.naturalWidth;
      if (actual < job.target) job.state.limit = actual;
      if (actual > job.delivered && job.image.isConnected &&
          (job.image.currentSrc || job.image.src) === job.current) {
        job.source.setAttribute('sizes', String(job.css) + 'px');
        job.source.setAttribute('srcset', job.url + ' ' + actual + 'w');
        if (job.source === job.image) job.image.setAttribute('src', job.url);
        job.image.setAttribute('data-mon-qualite', String(actual));
      }
      finish();
    };
    probe.src = job.url;
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(function () {
      scheduled = false;
      images.forEach(inspect);
    });
  }

  var observer = 'ResizeObserver' in window ? new ResizeObserver(schedule) : null;
  function register(root) {
    var found = root.matches && root.matches('img') ? [root] : root.querySelectorAll('img');
    Array.prototype.forEach.call(found, function (image) {
      if (images.has(image)) return;
      images.add(image);
      image.addEventListener('load', schedule);
      if (observer) observer.observe(image);
    });
    schedule();
  }

  register(document);
  window.addEventListener('scroll', schedule, { passive: true, capture: true });
  window.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('transitionend', schedule, true);
  document.addEventListener('shopify:section:load', function (event) { register(event.target); });
  new MutationObserver(function (entries) {
    entries.forEach(function (entry) {
      entry.addedNodes.forEach(function (node) { if (node.nodeType === 1) register(node); });
      entry.removedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        var removed = node.matches('img') ? [node] : node.querySelectorAll('img');
        Array.prototype.forEach.call(removed, function (image) {
          if (image.isConnected) return;
          images.delete(image);
          if (observer) observer.unobserve(image);
        });
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
